import { useState, useCallback, useMemo } from 'react';
import type { ContentPack, BuildState } from '../../packs/types';
import type { Adapters } from '../../adapters/types';
import { randomName } from '../wordlist';
import { useFleet } from './useFleet';
import { useShareURL } from './useShareURL';
import { buildPalette, hslToHex } from '../../pixel/palette';
import { DEFAULT_COLORS, randomColors, type ColorState } from '../colors';

export function useComposerState(pack: ContentPack, adapters: Adapters) {
  const [build, setBuild] = useState<BuildState>(() => ({ ...pack.defaultBuild }));
  const [colors, setColors] = useState<ColorState>(DEFAULT_COLORS);

  const baseCategoryId = pack.metadata?.baseCategoryId ?? 'hull';

  const background = useMemo(
    () => hslToHex(colors.backgroundHue, Math.round(35 * colors.bgSat / 100), 5),
    [colors.backgroundHue, colors.bgSat],
  );
  const palette = useMemo(
    () => buildPalette(colors.hue, colors.accentHue, colors.glowHue, colors.detailHue, colors.trimHue, {
      primary: colors.primarySat, accent: colors.accentSat, glow: colors.glowSat,
      detail: colors.detailSat, trim: colors.trimSat,
    }),
    [colors.hue, colors.accentHue, colors.glowHue, colors.detailHue, colors.trimHue,
     colors.primarySat, colors.accentSat, colors.glowSat, colors.detailSat, colors.trimSat],
  );

  const { fleet, load: loadFleet, save: saveFleet, remove: removeFleet } = useFleet(adapters.storage);

  const selectPart = useCallback((categoryId: string, key: string | null) => {
    setBuild(s => ({ ...s, [categoryId]: key }));
  }, []);

  const randomize = useCallback(() => {
    const baseKeys = Object.keys(pack.hulls);
    const newBuild: BuildState = { [baseCategoryId]: baseKeys[Math.floor(Math.random() * baseKeys.length)] };
    for (const cat of pack.categories) {
      if (cat.id === baseCategoryId) continue;
      newBuild[cat.id] = (cat.optional && Math.random() < 0.4) ? null : cat.keys[Math.floor(Math.random() * cat.keys.length)];
    }
    newBuild.name = randomName();
    setBuild(newBuild);
    setColors(randomColors());
  }, [pack, baseCategoryId]);

  // Sync share URL
  useShareURL(pack, adapters.navigation, { build, colors }, (payload) => {
    if (payload.packId !== pack.id) return;
    setBuild(payload.build);
    setColors(payload.colors);
  });

  return {
    state: { build, colors, background, palette, baseCategoryId },
    setBuild,
    setColors,
    actions: { selectPart, randomize, loadFleet, saveFleet, removeFleet },
    fleet,
  };
}

export type { ColorState };
